import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import Header from "./Header";
import { formatDateText, dateDifference } from "./CommonFunctions";
import MakePayment from "./MakePayment";
import AddReview from "./AddReview";
import TenancyDetailsByLTTI from "./TenancyDetailsByLTTI";
import CustomerReviewModal from "./CustomerReviewModal";
import AddFollowUp from "./AddFollowUp";
import SendSMS from "./SendSMS";
import { Link, useNavigate } from "react-router-dom";
import BatchCheckinPrintDialog from "./BatchCheckinPrintDialog";

export default function AllTenants() {
  const [tenants, setTenants] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedTenant, setSelectedTenant] = useState(null);

  // Restored legacy states
  const [showMakePaymentModal, setShowMakePaymentModal] = useState(false);
  const [showSendMessageModal, setShowSendMessageModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [tenantId, setTenantId] = useState("");
  const [longTermTenancyId, setLongTermTenancyId] = useState(null);
  const [rowDataProp, setRowDataProp] = useState({});
  const [activeTenancyDetails, setActiveTenancyDetails] = useState([]);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const url = process.env.REACT_APP_SERVER_URL;
  const [showBatchPrintDialog, setShowBatchPrintDialog] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    getActiveTenants();
  }, []);

const checkoutToday = tenants
  .filter((t) => t.stayDuration <= 0)
  .sort((a, b) => new Date(b.checkout) - new Date(a.checkout));

  const getActiveTenants = async () => {
    try {
      const response = await axios.get(`${url}/activeTenants`);
      setTenants(response.data);
      setActiveTenancyDetails(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  // Filter by search text
  const filteredTenants = tenants.filter((t) => {
    const term = search.toLowerCase();
    return (
      (t.names && t.names.toLowerCase().includes(term)) ||
      (t.roomid && t.roomid.toString().toLowerCase().includes(term)) ||
      (t.phoneNumbers && t.phoneNumbers.toLowerCase().includes(term))
    );
  });

  const overstayed = tenants.filter((t) => t.stayDuration < 0).length;
  const today = tenants.filter((t) => t.stayDuration === 0).length;
  const tomorrow = tenants.filter((t) => t.stayDuration === 1).length;
  const totalRent = tenants.reduce((acc, t) => acc + (t.rate || 0), 0);

  const propsForMakePayment = {
    tenancyId: selectedTenant?.id,
  };

  const columns = [
    {
  field: "roomid",
  headerName: "Room",
  width: 80,
  headerAlign: "center",
  align: "center",
  sortable: true,
  renderCell: (params) => {
    const row = params.row;
    return (
      <Typography
        fontWeight={600}
        color="primary"
        sx={{
          cursor: "pointer",
          textDecoration: "underline",
          "&:hover": { color: "primary.dark" },
        }}
        onClick={() => {
          // Same behavior as before (navigate to tenant details)
          navigate(`/tenants/add/${row.id}`, {
            state: { tenancyId: row.id },
          });
        }}
      >
        {params.value}
      </Typography>
    );
  },
},

    {
      field: "stayDuration",
      headerName: "Stay Status",
      width: 160,
      // headerAlign: "center",
      renderCell: (params) => {
        const val = params.value;
        if (val < 0)
          return (
            <Typography variant="body2" color="error" fontWeight={500}>
              Overstayed {val}
            </Typography>
          );
        if (val === 0)
          return (
            <Typography variant="body2" color="warning.main" fontWeight={500}>
              Checkout Today
            </Typography>
          );
        if (val === 1)
          return (
            <Typography variant="body2" color="info.main" fontWeight={500}>
              Checkout Tomorrow
            </Typography>
          );
        return <Typography variant="body2">{val} days left</Typography>;
      },
    },
    {
      field: "firstArrivalDate",
      headerName: "Initial Arrival",
      width: 150,
      // headerAlign: "center",
      renderCell: (params) => (
        <Typography variant="body2">
          {formatDateText(params.value)}
          <br />
          <span style={{ color: "#666", fontSize: "0.8rem" }}>
            ({dateDifference(params.value, new Date())} days since)
          </span>
        </Typography>
      ),
    },
    {
      field: "names",
      headerName: "Guest(s)",
      width: 150,
      // headerAlign: "center",
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={500}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "phoneNumbers",
      headerName: "Phone",
      width: 160,
      // headerAlign: "center",
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            textAlign: "center",
          }}
        >
          {params.value || "—"}
        </Typography>
      ),
    },
    {
      field: "checkin",
      headerName: "Check-In",
      width: 140,
      // headerAlign: "center",
      renderCell: (params) => (
        <Typography variant="body2">{formatDateText(params.value)}</Typography>
      ),
    },
    {
      field: "checkout",
      headerName: "Checkout",
      width: 140,
      // headerAlign: "center",
      renderCell: (params) => (
        <Typography variant="body2">{formatDateText(params.value)}</Typography>
      ),
    },
    {
      field: "rate",
      headerName: "Rent ($)",
      width: 100,
      // headerAlign: "center",
      renderCell: (params) => (
        <Typography fontWeight={600}>${params.value || 0}</Typography>
      ),
    },
    {
      field: "tenancyDescription",
      headerName: "Notes",
      width: 220,
      // headerAlign: "center",
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          {params.value || "—"}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 170,
      // headerAlign: "center",
      // align: "center",
      sortable: false,
      renderCell: (params) => {
        const row = params.row;
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              lineHeight: 1.3,
              textAlign: "center",
            }}
          >
            <Link
              onClick={() => {
                setSelectedTenant(row);
                setShowMakePaymentModal(true);
              }}
              sx={{ display: "block", cursor: "pointer", mb: 0.3 }}
            >
              Take Payment
            </Link>

            <Link
              onClick={() => {
                setLongTermTenancyId(row.longTermTenancyId);
                setShowSummaryModal(true);
              }}
              sx={{ display: "block", cursor: "pointer", mb: 0.3 }}
            >
              Payment Summary
            </Link>

            <Link
              onClick={() => {
                setTenantId(row.id);
                setShowReview(true);
              }}
              sx={{ display: "block", cursor: "pointer", mb: 0.3 }}
            >
              Review Guest
            </Link>
            

            {row.stayDuration <= 1 && (
              <>
                {row.renewing === 1 ? (
                  <b style={{color:"green"}} fontWeight={600}>
                    Renewing
                  </b>
                ) : row.renewing === 0 ? (
                  <b style={{color:"red"}} fontWeight={600}>
                    Not Renewing
                  </b>
                ) : (
                  <Link
                    onClick={() => {
                      setRowDataProp(row);
                      setShowFollowUpModal(true);
                    }}
                    sx={{
                      display: "block",
                      cursor: "pointer",
                      color: "warning.main",
                      fontWeight: 500,
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Follow Up
                  </Link>

                  
                  
                )}
                <Link
                  to={`/booking/${row.id}`}
                  sx={{ display: "block", cursor: "pointer", mb: 0.3 }}
                >
                  Enhanced Tenancy 
                </Link>
              </>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <Header />
      <Box sx={{ px: 4, py: 3, overflowX: "auto" }}>
        {/* ====== Summary Header ====== */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          mb={2}
        >
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ color: "primary.main", letterSpacing: 0.5 }}
          >
            Today's Guests Overview
          </Typography>

          <Box display="flex" gap={1.5} flexWrap="wrap" justifyContent="flex-end">
            <Box sx={{ backgroundColor: "#e3f2fd", color: "#1976d2", px: 1.5, py: 0.7, borderRadius: 2, fontWeight: 600 }}>
              Active Guests: {tenants.length}
            </Box>
            <Box sx={{ backgroundColor: "#fdecea", color: "#d32f2f", px: 1.5, py: 0.7, borderRadius: 2, fontWeight: 600 }}>
              Overstayed: {overstayed}
            </Box>
            <Box sx={{ backgroundColor: "#fff3e0", color: "#ed6c02", px: 1.5, py: 0.7, borderRadius: 2, fontWeight: 600 }}>
              Checkout Today: {today}
            </Box>
            <Box sx={{ backgroundColor: "#e1f5fe", color: "#0288d1", px: 1.5, py: 0.7, borderRadius: 2, fontWeight: 600 }}>
              Checkout Tomorrow: {tomorrow}
            </Box>
            <Box sx={{ backgroundColor: "#e8f5e9", color: "#2e7d32", px: 1.5, py: 0.7, borderRadius: 2, fontWeight: 600 }}>
              Total Rent: ${totalRent}
            </Box>
            
          </Box>
        </Box>

        <Button
          variant="contained"
          size="small"
          onClick={() => setShowBatchPrintDialog(true)}
          sx={{ borderRadius: 2, fontWeight: 600, textTransform: "none" }}
        >
          Print Today's Forms
        </Button>

        {/* ====== Table ====== */}
        <DataGrid
          rows={filteredTenants}
          columns={columns}
          getRowId={(row) => row.id}
          autoHeight
          disableRowSelectionOnClick
          pageSize={10}
          rowsPerPageOptions={[10, 20]}
          getRowHeight={() => "auto"}
          initialState={{
            sorting: { sortModel: [{ field: "stayDuration", sort: "asc" }] },
          }}
          sx={{
            border: "none",
            minWidth: "1100px",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f3f4f6",
              fontWeight: 700,
              fontSize: "0.95rem",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #e0e0e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            },
            "& .MuiDataGrid-row:nth-of-type(odd)": {
              backgroundColor: "#fafafa",
            },
            "& .MuiDataGrid-row:nth-of-type(even)": {
              backgroundColor: "#ffffff",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#f0f7ff",
              transition: "background 0.2s ease",
            },
          }}
        />
      </Box>

      {/* ====== Restored Modals ====== */}
      {showReview && (
        <CustomerReviewModal
          data={{ showReview, setShowReview, tenantId }}
        />
      )}

      {showMakePaymentModal && (
        <MakePayment value={propsForMakePayment} />
      )}

      {showSummaryModal && (
        <TenancyDetailsByLTTI
          longTermTenancyId={longTermTenancyId}
          showTenancyDetailsByLTTIModal={showSummaryModal}
          setShowTenancyDetailsByLTTIModal={setShowSummaryModal}
        />
      )}

      {showSendMessageModal && <SendSMS />}

      {showFollowUpModal && (
        <AddFollowUp
          showFollowUpModal={showFollowUpModal}
          data={rowDataProp}
          activeTenancyDetails={activeTenancyDetails}
          setShowFollowUpModal={setShowFollowUpModal}
        />
      )}

      <BatchCheckinPrintDialog
        open={showBatchPrintDialog}
        onClose={() => setShowBatchPrintDialog(false)}
        tenants={checkoutToday}
      />
    </>
  );
}
